import { useMemo, useState } from 'react';
import { useDatos } from '../datos/DatosContext';
import { useRuta } from '../lib/ruta';
import { buscar } from '../lib/buscar';
import { fechaLarga, formatearFecha, saludo } from '../lib/fechas';
import { Icono, type NombreIcono } from '../componentes/ui/Icono';
import { TituloSeccion } from '../componentes/ui/basicos';

interface ParaRetomar {
  clave: string;
  antetitulo: string;
  titulo: string;
  meta: string;
  icono: NombreIcono;
  ruta: string;
}

export function Inicio() {
  const { datos } = useDatos();
  const { ir } = useRuta();
  const [consulta, setConsulta] = useState('');
  const hoy = new Date();

  const resultados = useMemo(() => buscar(datos, consulta), [datos, consulta]);
  const hayConsulta = consulta.trim().length > 0;
  const fijadas = datos.notas.filter((n) => n.fijada && n.activa && !n.archivada);
  const rs = datos.cartas.find((c) => c.id === 'rs2627');

  const retomar: ParaRetomar[] = [];
  const actividad = datos.actividades.find((c) => c.estado === 'En curso') ?? datos.actividades.find((c) => c.estado === 'Pendiente');
  if (actividad) retomar.push({ clave: 'a', antetitulo: 'Práctica ' + actividad.estado.toLowerCase(), titulo: actividad.titulo, meta: actividad.curso, icono: 'leaf', ruta: '/cursos/' + actividad.id });
  const lectura = datos.biblioteca.find((b) => b.estado === 'En curso');
  if (lectura) retomar.push({ clave: 'b', antetitulo: 'Escuchando ahora', titulo: lectura.titulo, meta: lectura.tipo + ' · ' + lectura.autor, icono: 'book', ruta: '/biblioteca/' + lectura.id });
  const ultimo = datos.suenos[0];
  if (ultimo) retomar.push({ clave: 's', antetitulo: 'Último sueño', titulo: ultimo.titulo, meta: formatearFecha(ultimo.fecha), icono: 'moon', ruta: '/suenos/' + ultimo.id });

  const pendientes = datos.bandeja.filter((i) => i.estado !== 'Organizado').length;

  return (
    <div className="stack-lg">
      <header>
        <p className="eyebrow">{fechaLarga(hoy)}</p>
        <h1 className="display">{saludo(hoy)}</h1>
        <p className="muted" style={{ margin: '6px 0 0' }}>Un espacio para volver a ti. Hoy no hace falta completar nada.</p>
        <div className="ornament" aria-hidden="true"><Icono nombre="leaf" tam="sm" /></div>
      </header>

      <div className="stack-sm">
        <div className="searchbox">
          <Icono nombre="search" />
          <label htmlFor="buscar" className="sr">Buscar en todo mi universo</label>
          <input id="buscar" type="search" placeholder="Buscar sueños, notas, lecturas…" value={consulta} onChange={(e) => setConsulta(e.target.value)} />
        </div>
        {hayConsulta ? (
          <div className="card" style={{ padding: '8px 18px' }}>
            {resultados.map((r, i) => (
              <button key={i} className="result" onClick={() => ir(r.ruta)}><span>{r.etiqueta}</span><span className="tag">{r.modulo}</span></button>
            ))}
            {!resultados.length ? <p className="muted small" style={{ margin: '12px 0' }}>No encuentro nada con ese texto.</p> : null}
            <p className="muted small" style={{ margin: '10px 0 6px' }}>Búsqueda sin IA: solo compara títulos y textos.</p>
          </div>
        ) : null}
      </div>

      <section className="stack-sm" aria-labelledby="h-quick">
        <h2 id="h-quick" className="eyebrow">¿Qué quieres hacer?</h2>
        <div className="quick">
          <button className="tile" onClick={() => ir('/bandeja')}><span className="qicon amber"><Icono nombre="plus" tam="lg" /></span>Capturar una idea</button>
          <button className="tile" onClick={() => ir('/suenos/nuevo')}><span className="qicon dusk"><Icono nombre="moon" tam="lg" /></span>Registrar un sueño</button>
          <button className="tile" onClick={() => ir('/diario')}><span className="qicon"><Icono nombre="heart" tam="lg" /></span>¿Cómo me siento?</button>
        </div>
      </section>

      <div className="home-grid">
        <div className="stack-lg">
          <section className="stack-sm" aria-labelledby="h-pins">
            <div className="between">
              <TituloSeccion id="h-pins">Mis notas fijadas</TituloSeccion>
              <button className="btn quiet small" onClick={() => ir('/notas')}>Ver todas</button>
            </div>
            {fijadas.length ? (
              <div className="pins">
                {fijadas.map((n) => (
                  <article key={n.id} className="pin"><span className="tag moss">{n.tipo}</span><p className="pin-text">{n.texto}</p></article>
                ))}
              </div>
            ) : (
              <p className="muted">No tienes notas fijadas. Puedes fijarlas desde Notas destacadas.</p>
            )}
          </section>

          <section className="stack-sm" aria-labelledby="h-cont">
            <TituloSeccion id="h-cont">Para retomar</TituloSeccion>
            {retomar.map((c) => (
              <button key={c.clave} className="listbtn" onClick={() => ir(c.ruta)}>
                <span className="row"><Icono nombre={c.icono} tam="sm" /><span className="eyebrow">{c.antetitulo}</span></span>
                <span style={{ fontWeight: 600 }}>{c.titulo}</span>
                <span className="muted small">{c.meta}</span>
              </button>
            ))}
          </section>
        </div>

        <div className="stack">
          {rs ? (
            <button className="tile" onClick={() => ir('/astrologia')} style={{ background: 'var(--dusk-soft)', borderColor: 'transparent', boxShadow: 'none' }}>
              <span className="row" style={{ color: 'var(--dusk)' }}><Icono nombre="orbit" /><span className="eyebrow" style={{ color: 'var(--dusk)' }}>Mi ciclo actual</span></span>
              <span className="display" style={{ fontSize: 22 }}>Revolución solar 2026/27</span>
              <span className="small">Ejemplo ficticio · Melbourne · {rs.sistemaCasas}</span>
              <span className="small muted">Separada siempre de la carta natal.</span>
            </button>
          ) : null}
          <button className="tile" onClick={() => ir('/reflexiones')}>
            <span className="row"><Icono nombre="spark" /><span className="eyebrow">Reflexiones con IA</span></span>
            <span style={{ fontWeight: 600 }}>Conversar sobre lo que has autorizado</span>
            <span className="small muted">Tú eliges qué puede consultar en cada conversación.</span>
          </button>
          <button className="tile" onClick={() => ir('/bandeja')}>
            <span className="row"><Icono nombre="inbox" /><span className="eyebrow">Bandeja de entrada</span></span>
            <span style={{ fontWeight: 600 }}>{pendientes === 1 ? '1 cosa pendiente de ordenar' : pendientes + ' cosas pendientes de ordenar'}</span>
            <span className="small muted">Captura ahora, ordena cuando tengas tiempo.</span>
          </button>
        </div>
      </div>
    </div>
  );
}
